import {
  Button,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@solinkcorp/solink-ui';
import { Printer } from 'lucide-react';
import { useEffect } from 'react';
import EventDetails from '../EventsPage/EventDetails';
import { PrintThumbnailsAspen } from './PrintThumbnailsAspen';
import { usePrintPanelDialog, type PrintPanelProps } from './usePrintPanelDialog';

/** The dialog portals to `body`. Hide every other `body` child on the printed page. */
const PRINT_ONLY_DIALOG = 'print:[&>*:not([role=dialog])]:hidden';

/**
 * Browsers drop background colours when they print. Dark tokens would print light
 * text on white paper, so the dialog prints with the light tokens.
 */
const PRINT_LIGHT_TOKENS = [
  'print:[color-scheme:light]',
  'print:[--background-base:var(--White)]',
  'print:[--background-muted:var(--Gray-50)]',
  'print:[--background-elevated:var(--Gray-50)]',
  'print:[--foreground-primary:var(--Gray-900)]',
  'print:[--foreground-secondary:var(--Gray-600)]',
  'print:[--border-muted:var(--Gray-200)]',
].join(' ');

export function PrintPanelAspen({ printPanelOpen, event, closePanel }: PrintPanelProps) {
  const {
    locale,
    location,
    modalCloseAction,
    handlePrintClick,
    subtitle,
    title,
    description,
    date,
    timeDisplay,
    timezone,
  } = usePrintPanelDialog({ event, closePanel });

  useEffect(() => {
    if (!printPanelOpen) {
      return;
    }
    document.body.classList.add(PRINT_ONLY_DIALOG);
    return () => document.body.classList.remove(PRINT_ONLY_DIALOG);
  }, [printPanelOpen]);

  return (
    <Dialog
      open={Boolean(printPanelOpen)}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          modalCloseAction();
        }
      }}
    >
      <DialogContent
        size="m"
        className={cn(
          'flex max-h-[90vh] flex-col overflow-hidden',
          // The legacy print stylesheet sets `visibility: hidden` on `body` in print.
          'print:visible print:static print:max-h-none print:translate-x-0 print:translate-y-0 print:overflow-visible print:border-0 print:shadow-none',
          PRINT_LIGHT_TOKENS,
        )}
      >
        <DialogHeader className="shrink-0 print:hidden">
          <DialogTitle>{locale.print.printButton}</DialogTitle>
          <DialogDescription className="sr-only">{locale.print.printButton}</DialogDescription>
        </DialogHeader>
        {/* A scrolling flex child can shrink to nothing. Keep each block at its content height. */}
        <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 print:mx-0 print:h-auto print:max-h-none print:overflow-visible print:px-0 [&>*]:shrink-0">
          <div className="flex flex-col gap-1">
            {title && <h3 className="text-foreground-primary heading-lg">{title}</h3>}
            {subtitle && <p className="text-foreground-secondary body-md">{subtitle}</p>}
            <p className="text-foreground-secondary body-sm">
              <span>{date}</span>
              <span aria-hidden="true"> · </span>
              <span>{timeDisplay}</span>
            </p>
          </div>
          {location && (
            <PrintThumbnailsAspen key={event.id} eventCameras={event.cameras} location={location} />
          )}
          {description && (
            <p className="whitespace-pre-wrap break-words text-foreground-primary body-md">
              {description}
            </p>
          )}
          <Card variant="muted" padding="sm" className="empty:hidden">
            <EventDetails event={event} timezone={timezone} variant="print" fieldSet="receipt" />
          </Card>
          <div className="flex flex-col divide-y divide-border-muted border-y border-border-muted empty:hidden">
            <EventDetails event={event} timezone={timezone} variant="print" fieldSet="details" />
          </div>
        </div>
        <DialogFooter className="shrink-0 print:hidden">
          <Button type="button" variant="outline" onClick={modalCloseAction}>
            {locale.general.close}
          </Button>
          <Button type="button" onClick={handlePrintClick}>
            <Printer className="size-4" aria-hidden />
            {locale.print.printButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
